
import React from 'react';
import { Button } from '@/components/ui/button';
import { LogOut, Home, User } from 'lucide-react';
import { Image } from '@/components/ui/image';
import { useIsMobile } from '@/hooks/use-mobile';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';
import { ConnectionStatusStrict } from '@/hooks/useConnectionState';

interface AdminHeaderProps {
  onLogout: () => void;
  adminName?: string;
  lastSyncTime?: string;
  connectionStatus: ConnectionStatusStrict;
  retryConnection: () => Promise<boolean>;
}

const AdminHeader = ({ 
  onLogout, 
  adminName = 'Admin', 
  lastSyncTime,
  connectionStatus,
  retryConnection
}: AdminHeaderProps) => {
  const isMobile = useIsMobile();
  
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-slate-100 dark:border-gray-800 relative z-10">
      <div className="flex items-center justify-between p-2">
        {/* Logo and title */}
        <div className="flex items-center gap-2">
          <div className="relative bg-white rounded-md p-1 shadow-sm w-7 h-7 border border-amber-400">
            <Image 
              src="/lovable-uploads/46f6437f-3898-4fda-a4e1-d9b6194ac522.png"
              alt="THPT Chuyên Lê Hồng Phong Logo 2025"
              className="w-full h-full object-contain"
              fallback={
                <div className="w-full h-full flex items-center justify-center bg-blue-50 rounded-full">
                  <span className="text-blue-500 font-bold text-xs">LHP</span>
                </div>
              }
            />
          </div>
          <h1 className="text-base md:text-lg font-bold text-primary">Quản lý LHP</h1>
        </div>
        
        {/* Admin info and buttons */}
        <div className="flex items-center gap-1.5 relative z-20">
          <div className="flex items-center gap-1 text-xs text-muted-foreground bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded-full shadow-sm border border-slate-200 dark:border-gray-700">
            <User size={10} className="text-primary/70" />
            <span className="font-medium text-primary/90 text-xs">{adminName}</span>
            {lastSyncTime && !isMobile && (
              <span className="text-xs ml-1 opacity-70 hidden md:inline">
                {lastSyncTime}
              </span>
            )}
          </div>
          
          <ConnectionStatusIndicator 
            connectionStatus={connectionStatus}
            retryConnection={retryConnection}
          />
          
          <div className="flex gap-1 relative z-30">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.href = '/'} 
              className="h-6 md:h-7 bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 shadow-sm hover:bg-slate-50 dark:hover:bg-gray-700 px-1.5 md:px-2 min-w-[32px] relative z-20 active:scale-95 transition-transform"
            >
              <Home size={isMobile ? 14 : 15} className="mr-0.5 md:mr-1 text-primary/70" />
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium`}>Trang chủ</span>
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={onLogout}
              className="h-6 md:h-7 flex items-center gap-0.5 md:gap-1 shadow-sm bg-red-500 hover:bg-red-600 px-1.5 md:px-2 min-w-[32px] relative z-20 active:scale-95 transition-transform"
            >
              <LogOut size={isMobile ? 14 : 15} />
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium`}>Đăng xuất</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHeader;
